(function () {
  'use strict';

  if (window.customElements.get('shba-sticky-product-bar')) {
    return;
  }

  class ShbaStickyProductBar extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.sectionId = this.dataset.sectionId;
      this.section = document.querySelector(
        '.Product[data-section-id="' + this.sectionId + '"]'
      );
      this.form = this.section && this.section.querySelector('form.ProductForm');
      this.mainButton = this.form && this.form.querySelector('.ProductForm__AddToCart');
      this.submitButton = this.querySelector('[data-shba-sticky-submit]');
      this.submitLabel = this.querySelector('[data-shba-sticky-submit-label]');
      this.submitPrice = this.querySelector('[data-shba-sticky-submit-price]');
      this.price = this.querySelector('[data-shba-sticky-price]');
      this.image = this.querySelector('[data-shba-sticky-image]');
      this.status = this.querySelector('[data-shba-sticky-status]');
      this.optionSelects = Array.prototype.slice.call(
        this.querySelectorAll('[data-shba-sticky-option]')
      );
      this.footer = document.querySelector('.Footer') || document.querySelector('footer');
      this.cartDrawer = document.getElementById('sidebar-cart');

      if (!this.section || !this.form || !this.mainButton || !this.submitButton) {
        return;
      }

      this.initialized = true;

      this.onVariantChanged = this.handleVariantChanged.bind(this);
      this.onProductAdded = this.handleProductAdded.bind(this);
      this.onStickySubmit = this.handleStickySubmit.bind(this);
      this.onNativeFormChange = this.handleNativeFormChange.bind(this);
      this.onOptionChange = this.handleOptionChange.bind(this);
      this.onViewportChange = this.handleViewportChange.bind(this);

      document.addEventListener('variant:changed', this.onVariantChanged);
      document.addEventListener('product:added', this.onProductAdded);
      this.form.addEventListener('change', this.onNativeFormChange);
      this.submitButton.addEventListener('click', this.onStickySubmit);
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      this.optionSelects.forEach((select) => {
        select.addEventListener('change', this.onOptionChange);
      });

      this.observeMainButton();
      this.observeFooter();
      this.observeCartDrawer();
      this.observeChat();
      this.observeNativeButton();
      this.observeNativeErrors();
      this.observeProductSection();
      this.syncOptionsFromNativeForm();
      this.syncOptionAvailabilityFromNativeForm();
      this.syncButtonFromNativeForm();
      this.handleViewportChange();

      this.removeAttribute('hidden');
      this.setVisible(false);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariantChanged);
      document.removeEventListener('product:added', this.onProductAdded);

      if (this.form) {
        this.form.removeEventListener('change', this.onNativeFormChange);
      }

      if (this.submitButton) {
        this.submitButton.removeEventListener('click', this.onStickySubmit);
      }

      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);

      this.optionSelects.forEach((select) => {
        select.removeEventListener('change', this.onOptionChange);
      });

      if (this.mainButtonObserver) this.mainButtonObserver.disconnect();
      if (this.footerObserver) this.footerObserver.disconnect();
      if (this.cartDrawerObserver) this.cartDrawerObserver.disconnect();
      if (this.chatHostObserver) this.chatHostObserver.disconnect();
      if (this.chatDiscoveryObserver) this.chatDiscoveryObserver.disconnect();
      if (this.nativeButtonObserver) this.nativeButtonObserver.disconnect();
      if (this.nativeErrorObserver) this.nativeErrorObserver.disconnect();
      if (this.sectionObserver) this.sectionObserver.disconnect();
      if (this.resetTimer) window.clearTimeout(this.resetTimer);
      if (this.pendingTimer) window.clearTimeout(this.pendingTimer);
      if (this.viewportFrame) window.cancelAnimationFrame(this.viewportFrame);

      document.documentElement.classList.remove('shba-sticky-atc-visible');
      this.initialized = false;
    }

    observeMainButton() {
      this.mainButtonObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (!entry) return;

          if (entry.isIntersecting) {
            this.hasSeenMainButton = true;
          }

          this.mainButtonIsAboveViewport =
            !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;

          if (this.mainButtonIsAboveViewport) {
            this.hasSeenMainButton = true;
          }

          this.updateVisibility();
        },
        { threshold: [0, 0.01] }
      );

      this.mainButtonObserver.observe(this.mainButton);
    }

    observeFooter() {
      if (!this.footer) return;

      this.footerObserver = new IntersectionObserver(
        (entries) => {
          this.footerIsVisible = Boolean(entries[0] && entries[0].isIntersecting);
          this.updateVisibility();
        },
        { threshold: 0 }
      );

      this.footerObserver.observe(this.footer);
    }

    observeCartDrawer() {
      if (!this.cartDrawer) return;

      const updateCartState = () => {
        this.cartDrawerIsOpen = this.cartDrawer.getAttribute('aria-hidden') === 'false';
        this.updateVisibility();
      };

      this.cartDrawerObserver = new MutationObserver(updateCartState);
      this.cartDrawerObserver.observe(this.cartDrawer, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
      updateCartState();
    }

    observeChat() {
      const bindChatHost = () => {
        const nextChatHost = document.querySelector('shopify-chat');

        if (!nextChatHost || nextChatHost === this.chatHost) return;

        if (this.chatHostObserver) this.chatHostObserver.disconnect();
        if (this.chatDiscoveryObserver) {
          this.chatDiscoveryObserver.disconnect();
          this.chatDiscoveryObserver = null;
        }
        this.chatHost = nextChatHost;

        const updateChatState = () => {
          this.chatIsOpen = this.chatHost.hasAttribute('open');
          this.updateVisibility();
        };

        this.chatHostObserver = new MutationObserver(updateChatState);
        this.chatHostObserver.observe(this.chatHost, {
          attributes: true,
          attributeFilter: ['open']
        });
        updateChatState();
      };

      bindChatHost();

      if (!this.chatHost) {
        this.chatDiscoveryObserver = new MutationObserver(bindChatHost);
        this.chatDiscoveryObserver.observe(document.body, { childList: true });
      }
    }

    observeNativeButton() {
      this.nativeButtonObserver = new MutationObserver(() => {
        this.syncButtonFromNativeForm();
      });

      this.nativeButtonObserver.observe(this.mainButton, {
        attributes: true,
        attributeFilter: ['disabled', 'class', 'data-action'],
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    observeNativeErrors() {
      if (this.nativeErrorObserver) this.nativeErrorObserver.disconnect();

      this.nativeErrorObserver = new MutationObserver(() => {
        this.checkNativeError();
      });

      this.nativeErrorObserver.observe(this.form, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'aria-hidden'],
        childList: true,
        characterData: true,
        subtree: true
      });
      this.checkNativeError();
    }

    observeProductSection() {
      this.sectionObserver = new MutationObserver(() => {
        const nextForm = this.section.querySelector('form.ProductForm');
        const nextButton = nextForm && nextForm.querySelector('.ProductForm__AddToCart');

        if (
          nextForm &&
          nextButton &&
          (nextForm !== this.form || nextButton !== this.mainButton)
        ) {
          this.rebindNativeElements(nextForm, nextButton);
        }
      });

      this.sectionObserver.observe(this.section, {
        childList: true,
        subtree: true
      });
    }

    rebindNativeElements(nextForm, nextButton) {
      if (this.form) {
        this.form.removeEventListener('change', this.onNativeFormChange);
      }

      if (this.mainButtonObserver) this.mainButtonObserver.disconnect();
      if (this.nativeButtonObserver) this.nativeButtonObserver.disconnect();
      if (this.nativeErrorObserver) this.nativeErrorObserver.disconnect();

      this.form = nextForm;
      this.mainButton = nextButton;
      this.form.addEventListener('change', this.onNativeFormChange);
      this.observeMainButton();
      this.observeNativeButton();
      this.observeNativeErrors();
      this.syncOptionsFromNativeForm();
      this.syncOptionAvailabilityFromNativeForm();
      this.syncButtonFromNativeForm();
    }

    checkNativeError() {
      const errorElement = this.form.querySelector('.ProductForm__Error');
      const errorMessage = errorElement && errorElement.textContent.trim();
      const errorIsHidden = !errorElement ||
        errorElement.hidden ||
        errorElement.getAttribute('aria-hidden') === 'true' ||
        errorElement.offsetParent === null;

      if (!errorMessage || errorIsHidden) {
        this.lastNativeError = '';
        return;
      }

      if (errorMessage === this.lastNativeError) return;

      this.lastNativeError = errorMessage;
      const stickyAttemptPending = this.submitButton.getAttribute('aria-busy') === 'true';
      if (this.pendingTimer) window.clearTimeout(this.pendingTimer);
      this.submitButton.removeAttribute('aria-busy');
      this.syncButtonFromNativeForm();

      if (stickyAttemptPending) {
        this.announce('Could not add this item to the cart. ' + errorMessage);
        this.trackStickyResult('error');
      }
    }

    eventBelongsToProduct(event, variant) {
      const eventTarget = event.target;

      if (
        eventTarget &&
        eventTarget !== document &&
        eventTarget !== window &&
        !this.section.contains(eventTarget)
      ) {
        return false;
      }

      if (variant && !this.variantBelongsToProduct(variant.id)) {
        return false;
      }

      return Boolean(variant || (eventTarget && this.section.contains(eventTarget)));
    }

    variantBelongsToProduct(variantId) {
      const variantControls = this.form.querySelectorAll(
        'select[name="id"] option, input[name="id"]'
      );

      return Array.prototype.some.call(
        variantControls,
        (control) => String(control.value) === String(variantId)
      );
    }

    updateVisibility() {
      const shouldShow = Boolean(
        this.hasSeenMainButton &&
        this.mainButtonIsAboveViewport &&
        !this.footerIsVisible &&
        !this.cartDrawerIsOpen &&
        !this.chatIsOpen
      );

      this.setVisible(shouldShow);
    }

    handleViewportChange() {
      if (this.viewportFrame) return;

      this.viewportFrame = window.requestAnimationFrame(() => {
        this.viewportFrame = null;

        if (!this.mainButton || !this.mainButton.isConnected) return;

        const buttonRect = this.mainButton.getBoundingClientRect();
        const buttonIsInViewport =
          buttonRect.bottom > 0 && buttonRect.top < window.innerHeight;

        if (buttonIsInViewport) {
          this.hasSeenMainButton = true;
        }

        this.mainButtonIsAboveViewport = buttonRect.bottom <= 0;

        if (this.mainButtonIsAboveViewport) {
          this.hasSeenMainButton = true;
        }

        this.updateVisibility();
      });
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');

      if (visible) {
        this.removeAttribute('inert');
      } else {
        this.setAttribute('inert', '');
      }

      document.documentElement.classList.toggle('shba-sticky-atc-visible', visible);
    }

    handleVariantChanged(event) {
      const variant = event.detail && event.detail.variant;

      if (!this.eventBelongsToProduct(event, variant)) {
        return;
      }

      if (!variant) {
        this.submitButton.disabled = true;
        this.submitPrice.hidden = true;
        window.requestAnimationFrame(() => {
          this.syncOptionAvailabilityFromNativeForm();
          this.syncButtonFromNativeForm();
        });
        return;
      }

      this.dataset.variantId = variant.id;

      if (Array.isArray(variant.options)) {
        this.optionSelects.forEach((select) => {
          const optionIndex = Number(select.dataset.optionPosition) - 1;
          const nextValue = variant.options[optionIndex];

          if (typeof nextValue !== 'undefined') {
            select.value = nextValue;
          }
        });
      } else {
        this.syncOptionsFromNativeForm();
      }

      const formattedPrice = this.formatMoney(variant.price);
      this.price.textContent = formattedPrice;
      this.submitPrice.textContent = ' · ' + formattedPrice;
      this.submitPrice.hidden = !variant.available;

      if (this.image) {
        const featuredImage = variant.featured_image ||
          (variant.featured_media && variant.featured_media.preview_image);
        const imageUrl = featuredImage && (featuredImage.src || featuredImage.url);

        if (imageUrl) {
          this.image.removeAttribute('srcset');
          this.image.src = imageUrl;
        }
      }

      window.requestAnimationFrame(() => {
        this.syncOptionAvailabilityFromNativeForm();
        this.syncButtonFromNativeForm();
      });
    }

    handleNativeFormChange() {
      window.requestAnimationFrame(() => {
        this.syncOptionsFromNativeForm();
        this.syncOptionAvailabilityFromNativeForm();
        this.syncButtonFromNativeForm();
      });
    }

    handleOptionChange(event) {
      const stickySelect = event.currentTarget;
      const position = stickySelect.dataset.optionPosition;
      const value = stickySelect.value;
      const nativeControls = Array.prototype.slice.call(
        this.form.querySelectorAll('[data-option-position="' + position + '"]')
      );
      const nativeControl = nativeControls.find((control) => {
        const controlValue =
          control.value ||
          control.getAttribute('data-value') ||
          control.getAttribute('data-option-value');

        return controlValue === value && !control.disabled;
      });

      if (!nativeControl) {
        this.syncOptionsFromNativeForm();
        this.announce('That option is currently unavailable.');
        return;
      }

      if (nativeControl.matches('input[type="radio"], button')) {
        nativeControl.click();
      } else {
        nativeControl.value = value;
        nativeControl.dispatchEvent(new Event('input', { bubbles: true }));
        nativeControl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    syncOptionsFromNativeForm() {
      this.optionSelects.forEach((select) => {
        const position = select.dataset.optionPosition;
        const checkedControl = this.form.querySelector(
          'input[data-option-position="' + position + '"]:checked'
        );
        const selectedButton = this.form.querySelector(
          '[data-option-position="' + position + '"].is-selected'
        );
        const control = checkedControl || selectedButton;
        const value = control && (
          control.value ||
          control.getAttribute('data-value') ||
          control.getAttribute('data-option-value')
        );

        if (value && Array.prototype.some.call(select.options, (option) => option.value === value)) {
          select.value = value;
        }
      });
    }

    syncOptionAvailabilityFromNativeForm() {
      this.optionSelects.forEach((select) => {
        const position = select.dataset.optionPosition;
        const nativeControls = Array.prototype.slice.call(
          this.form.querySelectorAll('[data-option-position="' + position + '"]')
        );

        Array.prototype.forEach.call(select.options, (option) => {
          const nativeControl = nativeControls.find((control) => {
            const controlValue =
              control.value ||
              control.getAttribute('data-value') ||
              control.getAttribute('data-option-value');

            return controlValue === option.value;
          });

          option.disabled = !nativeControl ||
            nativeControl.disabled ||
            nativeControl.classList.contains('is-disabled') ||
            nativeControl.getAttribute('aria-disabled') === 'true';
        });
      });
    }

    syncButtonFromNativeForm() {
      const nativeButton = this.form.querySelector('.ProductForm__AddToCart');

      if (!nativeButton) return;

      if (nativeButton !== this.mainButton) {
        this.mainButton = nativeButton;
      }

      const nativeLabelElement = nativeButton.querySelector('span');
      const nativeLabel = (
        (nativeLabelElement && nativeLabelElement.textContent) ||
        nativeButton.textContent ||
        this.submitButton.dataset.addLabel
      ).trim();
      const isDisabled = nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true';

      this.submitLabel.textContent = nativeLabel;
      this.submitButton.disabled = isDisabled;
      this.submitPrice.hidden = isDisabled;
    }

    handleStickySubmit() {
      this.syncButtonFromNativeForm();

      if (this.submitButton.disabled || !this.mainButton) {
        this.announce('Select an available option before adding this item.');
        return;
      }

      this.trackStickyClick();
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.announce('Adding item to cart.');
      this.mainButton.click();

      if (this.pendingTimer) window.clearTimeout(this.pendingTimer);
      this.pendingTimer = window.setTimeout(() => {
        this.submitButton.removeAttribute('aria-busy');
        this.syncButtonFromNativeForm();
      }, 7000);
    }

    handleProductAdded(event) {
      const addedVariant = event.detail && event.detail.variant;

      if (!this.eventBelongsToProduct(event, addedVariant)) {
        return;
      }

      if (
        addedVariant &&
        this.dataset.variantId &&
        String(addedVariant.id) !== String(this.dataset.variantId)
      ) {
        return;
      }

      const stickyAttemptPending = this.submitButton.getAttribute('aria-busy') === 'true';

      if (!stickyAttemptPending) {
        this.syncButtonFromNativeForm();
        return;
      }

      if (this.pendingTimer) window.clearTimeout(this.pendingTimer);
      this.submitButton.removeAttribute('aria-busy');
      this.submitLabel.textContent = 'Added';
      this.submitPrice.hidden = true;
      this.announce('Item added to cart.');
      this.trackStickyResult('success');

      if (this.resetTimer) window.clearTimeout(this.resetTimer);
      this.resetTimer = window.setTimeout(() => {
        this.syncButtonFromNativeForm();
      }, 1600);
    }

    formatMoney(cents) {
      const numericCents = Number(cents);

      if (window.Currency && typeof window.Currency.formatMoney === 'function') {
        const formattedHtml = window.Currency.formatMoney(
          numericCents,
          this.dataset.moneyFormat
        );
        const temporaryElement = document.createElement('span');
        temporaryElement.innerHTML = formattedHtml;
        return temporaryElement.textContent.trim();
      }

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.dataset.currencyCode || 'USD'
        }).format(numericCents / 100);
      } catch (error) {
        return '$' + (numericCents / 100).toFixed(2);
      }
    }

    trackStickyClick() {
      const eventData = {
        event: 'shba_sticky_add_to_cart_click',
        product_id: this.dataset.productId,
        variant_id: this.dataset.variantId,
        device_type: window.matchMedia('(max-width: 820px)').matches ? 'mobile' : 'desktop'
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(eventData);

      if (typeof window.clarity === 'function') {
        window.clarity('event', 'sticky_add_to_cart_click');
      }
    }

    trackStickyResult(result) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'shba_sticky_add_to_cart_' + result,
        product_id: this.dataset.productId,
        variant_id: this.dataset.variantId,
        device_type: window.matchMedia('(max-width: 820px)').matches ? 'mobile' : 'desktop'
      });

      if (typeof window.clarity === 'function') {
        window.clarity('event', 'sticky_add_to_cart_' + result);
      }
    }

    announce(message) {
      if (!this.status) return;

      this.status.textContent = '';
      window.requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }
  }

  window.customElements.define('shba-sticky-product-bar', ShbaStickyProductBar);
})();
